import React, { useEffect, useRef, useMemo } from 'react';
import { EmptyState, SectionTitle, formatNumber } from './analyticsHelpers';

// ─── Viewport canvas heatmap ──────────────────────────────────────────────────
const MERCATOR_SIZE = 500;

const latToY = (lat, minLat, maxLat, h) =>
    h - ((lat - minLat) / (maxLat - minLat || 1)) * h;

const lngToX = (lng, minLng, maxLng, w) =>
    ((lng - minLng) / (maxLng - minLng || 1)) * w;

const ViewportHeatmap = ({ viewports }) => {
    const canvasRef = useRef(null);

    const points = useMemo(() => {
        if (!viewports || viewports.length === 0) return [];
        return viewports.map(v => ({ lat: v.centerLat, lng: v.centerLng }));
    }, [viewports]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || points.length === 0) return;

        const w = canvas.width;
        const h = canvas.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, w, h);

        const lats = points.map(p => p.lat);
        const lngs = points.map(p => p.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        const latPad = (maxLat - minLat) * 0.1 || 0.001;
        const lngPad = (maxLng - minLng) * 0.1 || 0.001;

        const effectiveMinLat = minLat - latPad;
        const effectiveMaxLat = maxLat + latPad;
        const effectiveMinLng = minLng - lngPad;
        const effectiveMaxLng = maxLng + lngPad;

        const offscreen = document.createElement('canvas');
        offscreen.width = w;
        offscreen.height = h;
        const octx = offscreen.getContext('2d');
        const radius = Math.max(20, Math.min(50, w / 12));

        points.forEach(({ lat, lng }) => {
            const x = lngToX(lng, effectiveMinLng, effectiveMaxLng, w);
            const y = latToY(lat, effectiveMinLat, effectiveMaxLat, h);

            const grad = octx.createRadialGradient(x, y, 0, x, y, radius);
            grad.addColorStop(0, 'rgba(0,0,0,0.3)');
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            octx.fillStyle = grad;
            octx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
        });

        const imgData = octx.getImageData(0, 0, w, h);
        const colorized = ctx.createImageData(w, h);

        for (let i = 0; i < imgData.data.length; i += 4) {
            const alpha = imgData.data[i + 3];
            if (alpha === 0) continue;
            const t = alpha / 255;
            let r, g, b;
            if (t < 0.25)      { r = 0;   g = Math.round(t * 4 * 200); b = 255; }
            else if (t < 0.5)  { r = 0;   g = 200; b = Math.round((0.5 - t) * 4 * 255); }
            else if (t < 0.75) { r = Math.round((t - 0.5) * 4 * 255); g = 200; b = 0; }
            else               { r = 255; g = Math.round((1 - t) * 4 * 200); b = 0; }
            colorized.data[i]     = r;
            colorized.data[i + 1] = g;
            colorized.data[i + 2] = b;
            colorized.data[i + 3] = Math.round(alpha * 0.8);
        }
        ctx.putImageData(colorized, 0, 0);

        ctx.fillStyle = 'rgba(15,27,45,0.5)';
        ctx.font = '11px DM Sans, sans-serif';
        ctx.fillText(`${points.length} viewport events`, 8, 16);
    }, [points]);

    if (points.length === 0) {
        return (
            <div className="viewport-canvas-wrap empty">
                <EmptyState message="No viewport data collected yet — attendees need to pan or zoom the map" />
            </div>
        );
    }

    return (
        <div className="viewport-canvas-wrap">
            <canvas
                ref={canvasRef}
                width={MERCATOR_SIZE}
                height={Math.round(MERCATOR_SIZE * 0.6)}
                className="viewport-canvas"
            />
            <p className="viewport-canvas-note">
                Each point = the center of an attendee's map view at a given moment
            </p>
        </div>
    );
};

// ─── Zoom level distribution ──────────────────────────────────────────────────
const ZoomDistribution = ({ viewports }) => {
    const distribution = useMemo(() => {
        if (!viewports || viewports.length === 0) return null;
        const counts = {};
        viewports.forEach(v => {
            const z = Math.round(v.zoomLevel);
            counts[z] = (counts[z] || 0) + 1;
        });
        const sorted = Object.entries(counts)
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([zoom, count]) => ({ zoom: Number(zoom), count }));
        const max = Math.max(...sorted.map(s => s.count), 1);
        const total = sorted.reduce((a, s) => a + s.count, 0);
        const avgZoom = viewports.reduce((a, v) => a + v.zoomLevel, 0) / viewports.length;
        return { sorted, max, total, avgZoom };
    }, [viewports]);

    if (!distribution) return <EmptyState message="No zoom data yet" />;

    return (
        <div>
            <p className="chart-annotation" style={{ marginBottom: '1rem' }}>
                Avg zoom level: <strong>{distribution.avgZoom.toFixed(1)}</strong>
                {distribution.avgZoom >= 17 && ' (street-level detail)'}
                {distribution.avgZoom >= 15 && distribution.avgZoom < 17 && ' (neighborhood view)'}
                {distribution.avgZoom < 15 && ' (wide area view)'}
            </p>
            <div className="zoom-chart">
                {distribution.sorted.map(({ zoom, count }) => (
                    <div key={zoom} className="zoom-row">
                        <span className="zoom-label">z{zoom}</span>
                        <div className="zoom-bar-bg">
                            <div
                                className="zoom-bar-fill"
                                style={{
                                    width: `${(count / distribution.max) * 100}%`,
                                    background: zoom >= 17
                                        ? '#10b981'
                                        : zoom >= 15
                                        ? '#2B8EE8'
                                        : '#f59e0b',
                                }}
                                title={`Zoom ${zoom}: ${count} events`}
                            />
                        </div>
                        <span className="zoom-count">{formatNumber(count)}</span>
                        <span className="zoom-pct">
                            {Math.round((count / distribution.total) * 100)}%
                        </span>
                    </div>
                ))}
            </div>
            <div className="zoom-legend-row">
                <span className="zoom-legend-item">
                    <span className="zoom-legend-dot" style={{ background: '#f59e0b' }} /> Overview (&lt;15)
                </span>
                <span className="zoom-legend-item">
                    <span className="zoom-legend-dot" style={{ background: '#2B8EE8' }} /> Neighborhood (15–16)
                </span>
                <span className="zoom-legend-item">
                    <span className="zoom-legend-dot" style={{ background: '#10b981' }} /> Street level (17+)
                </span>
            </div>
        </div>
    );
};

// ─── Avg zoom by time of day ──────────────────────────────────────────────────
const ZoomByHour = ({ viewports }) => {
    const hourlyAvg = useMemo(() => {
        if (!viewports || viewports.length === 0) return null;
        const buckets = Array.from({ length: 24 }, () => ({ sum: 0, count: 0 }));
        viewports.forEach(v => {
            const h = new Date(v.timestamp).getHours();
            buckets[h].sum += v.zoomLevel;
            buckets[h].count++;
        });
        return buckets.map(b => b.count > 0 ? b.sum / b.count : null);
    }, [viewports]);

    if (!hourlyAvg) return <EmptyState message="No viewport data yet" />;

    const values = hourlyAvg.filter(v => v !== null);
    if (values.length === 0) return <EmptyState message="No viewport data yet" />;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    return (
        <div>
            <div className="hourly-chart mini" style={{ height: '120px' }}>
                {hourlyAvg.map((avg, h) => {
                    if (avg === null) {
                        return (
                            <div key={h} className="hour-col">
                                <div className="hour-bar-wrap">
                                    <div className="hour-bar" style={{ height: '2px', opacity: 0.1, background: '#94A3B8' }} />
                                </div>
                                {h % 6 === 0 && <div className="hour-label">{h}:00</div>}
                            </div>
                        );
                    }
                    const heightPct = ((avg - min) / range) * 100;
                    return (
                        <div key={h} className="hour-col">
                            <div className="hour-bar-wrap">
                                <div
                                    className="hour-bar"
                                    style={{
                                        height: `${Math.max(heightPct, 5)}%`,
                                        background: avg >= 17 ? '#10b981' : avg >= 15 ? '#2B8EE8' : '#f59e0b',
                                        opacity: 0.7,
                                    }}
                                    title={`${h}:00 — avg zoom ${avg.toFixed(1)}`}
                                />
                            </div>
                            {h % 6 === 0 && <div className="hour-label">{h}:00</div>}
                        </div>
                    );
                })}
            </div>
            <p className="chart-annotation">Higher bars = attendees zooming in more at that hour</p>
        </div>
    );
};

// ─── Area engagement grid ─────────────────────────────────────────────────────
const AreaEngagementGrid = ({ viewports }) => {
    const grid = useMemo(() => {
        if (!viewports || viewports.length < 4) return null;

        const lats = viewports.map(v => v.centerLat);
        const lngs = viewports.map(v => v.centerLng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        const latRange = maxLat - minLat || 0.001;
        const lngRange = maxLng - minLng || 0.001;

        const cells = Array.from({ length: 4 }, () => Array(4).fill(0));

        viewports.forEach(v => {
            const col = Math.min(3, Math.floor(((v.centerLng - minLng) / lngRange) * 4));
            const row = Math.min(3, Math.floor(((maxLat - v.centerLat) / latRange) * 4));
            cells[row][col]++;
        });

        const maxCell = Math.max(...cells.flat(), 1);
        return { cells, maxCell };
    }, [viewports]);

    if (!grid) {
        return <EmptyState message="Need more viewport data to compute area engagement" />;
    }

    return (
        <div className="area-grid-wrap">
            <div className="area-grid">
                {grid.cells.map((row, ri) =>
                    row.map((count, ci) => {
                        const intensity = count / grid.maxCell;
                        return (
                            <div
                                key={`${ri}-${ci}`}
                                className="area-cell"
                                style={{
                                    background: count === 0
                                        ? 'var(--bg)'
                                        : `rgba(43,142,232,${0.08 + intensity * 0.85})`,
                                    color: intensity > 0.5 ? 'white' : 'var(--text-3)',
                                    borderColor: 'var(--border)',
                                }}
                                title={`Zone [${ri},${ci}]: ${count} views`}
                            >
                                {count > 0 && (
                                    <span className="area-cell-count">{count}</span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
            <p className="viewport-canvas-note">
                Venue divided into 16 zones · Darker = more attendee attention
            </p>
        </div>
    );
};

// ─── ZoomTab ──────────────────────────────────────────────────────────────────
const ZoomTab = ({ viewports }) => {
    const hasData = viewports && viewports.length > 0;

    return (
        <div className="ad-tab-content">

            <section className="ad-section">
                <div className="ad-card ad-card-fullwidth">
                    <SectionTitle sub="Where attendees were looking on the map">
                        Viewport Center Heatmap
                    </SectionTitle>
                    <ViewportHeatmap viewports={viewports} />
                </div>
            </section>

            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <SectionTitle sub="How zoomed-in attendees were">Zoom Level Distribution</SectionTitle>
                    <ZoomDistribution viewports={viewports} />
                </div>
                <div className="ad-card">
                    <SectionTitle sub="Which zones of the venue got the most map attention">
                        Area Engagement
                    </SectionTitle>
                    <AreaEngagementGrid viewports={viewports} />
                </div>
            </section>

            <section className="ad-section">
                <div className="ad-card">
                    <SectionTitle sub="Average zoom level per hour — higher = more focused exploration">
                        Zoom Depth by Hour
                    </SectionTitle>
                    <ZoomByHour viewports={viewports} />
                </div>
            </section>

            {!hasData && (
                <section className="ad-section">
                    <div className="ad-card">
                        <EmptyState message="Viewport data is collected as attendees pan and zoom the map. Check back once the event is live." />
                    </div>
                </section>
            )}

        </div>
    );
};

export default ZoomTab;